import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ToolBar } from "@/components/ToolBar";
import { ExportMenu } from "@/components/ExportMenu";
import { Plus, PencilSimple, DownloadSimple, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/roles";
import { ArchiveButton } from "@/components/ArchiveButton";

const EMPTY = { name: "", contact_person: "", email: "", phone: "", address: "", tax_id: "", contract_type: "", contract_value: 0, notes: "" };

export default function ClientsPage() {
  const { user } = useAuth();
  const canDelete = can(user, ["admin"]);
  const canTransfer = can(user, ["admin", "finance"]);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  async function load() {
    const { data } = await api.get("/clients", { params: { ...(search ? { search } : {}), ...(showArchived ? { include_archived: true } : {}) } });
    setItems(data);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [search, showArchived]);

  async function handleExport() {
    try {
      const token = localStorage.getItem("access_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/clients/export`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 16);
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `clients_export_${stamp}.json`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      toast.success("Clients exported");
    } catch { toast.error("Export failed"); }
  }

  async function handleImportFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const payload = JSON.parse(await file.text());
      const { data } = await api.post("/clients/import", { payload, mode: "upsert" });
      toast.success(`Imported: +${data.inserted} new / ${data.updated} updated`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed — is this a clients_export JSON?");
    }
  }

  async function submit(e) {
    e.preventDefault();
    try {
      const payload = { ...form, contract_value: form.contract_value ? Number(form.contract_value) : null };
      if (editing) { await api.put(`/clients/${editing.id}`, payload); toast.success("Client updated"); }
      else { await api.post("/clients", payload); toast.success("Client created"); }
      setOpen(false); setEditing(null); setForm(EMPTY); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed"); }
  }

  async function remove(c) {
    try {
      await api.delete(`/clients/${c.id}`);
      toast.success(`${c.name} archived`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Archive failed"); }
  }

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex items-end justify-between border-b border-border pb-6">
        <div>
          <div className="section-caption mb-2">Module 05</div>
          <h1 className="section-title text-3xl sm:text-4xl">Clients</h1>
          <p className="text-sm text-muted-foreground mt-1">Client contracts, contacts and history in one place.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canTransfer && (
            <>
              <Button variant="outline" onClick={handleExport} className="border-border" data-testid="clients-export-btn">
                <DownloadSimple size={16} className="mr-2" /> Export JSON
              </Button>
              <label className="inline-flex" data-testid="clients-import-label">
                <input type="file" accept="application/json,.json" onChange={handleImportFile} className="hidden" data-testid="clients-import-input" />
                <span className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-medium cursor-pointer hover:bg-muted">
                  <UploadSimple size={16} /> Import JSON
                </span>
              </label>
            </>
          )}
          <Button onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} data-testid="new-client-btn"><Plus size={16} className="mr-2" /> New Client</Button>
        </div>
      </div>

      <ToolBar search={search} onSearch={setSearch} placeholder="Search clients by name, email, contact…" showArchived={showArchived} onToggleArchived={setShowArchived} />

      <div className="border border-border rounded-md bg-card overflow-x-auto">
        <Table className="min-w-[900px]">
          <TableHeader>
            <TableRow>
              <TableHead>Client</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Contract</TableHead>
              <TableHead className="text-right">Value</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">No clients found.</TableCell></TableRow>
            ) : items.map((c) => (
              <TableRow key={c.id} data-testid={`client-row-${c.id}`}>
                <TableCell>
                  <Link to={`/clients/${c.id}`} className="font-medium hover:underline" data-testid={`open-client-${c.id}`}>{c.name}</Link>
                  <div className="text-xs text-muted-foreground">{c.address || "—"}</div>
                </TableCell>
                <TableCell>
                  <div className="text-sm">{c.contact_person || "—"}</div>
                  <div className="text-xs text-muted-foreground">{c.email || c.phone || ""}</div>
                </TableCell>
                <TableCell className="text-sm">{c.contract_type || "—"}</TableCell>
                <TableCell className="text-right tabular-nums">{c.contract_value ? `₱${Number(c.contract_value).toLocaleString()}` : "—"}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" className="border-border" onClick={() => { setEditing(c); setForm({ ...EMPTY, ...c }); setOpen(true); }} data-testid={`edit-client-${c.id}`}><PencilSimple size={14} /></Button>
                    <ExportMenu endpointPath={`/exports/client/${c.id}`} baseName={`client_${c.name.replace(/\s+/g,'_')}`} testId={`export-client-${c.id}`} />
                    {canDelete && (
                      <ArchiveButton endpoint={`/clients/${c.id}`} label={c.name} onArchived={load} testId={`archive-client-${c.id}`} />
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading font-black">{editing ? "Edit Client" : "New Client"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs uppercase tracking-widest text-muted-foreground">Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border-border" data-testid="client-name-input" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase tracking-widest text-muted-foreground">Contact Person</Label><Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="border-border" data-testid="client-contact-input" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase tracking-widest text-muted-foreground">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="border-border" data-testid="client-email-input" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase tracking-widest text-muted-foreground">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="border-border" data-testid="client-phone-input" /></div>
              <div className="space-y-1.5 col-span-2"><Label className="text-xs uppercase tracking-widest text-muted-foreground">Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="border-border" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase tracking-widest text-muted-foreground">Tax ID</Label><Input value={form.tax_id} onChange={(e) => setForm({ ...form, tax_id: e.target.value })} className="border-border" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase tracking-widest text-muted-foreground">Contract Type</Label><Input value={form.contract_type} onChange={(e) => setForm({ ...form, contract_type: e.target.value })} className="border-border" /></div>
              <div className="space-y-1.5 col-span-2"><Label className="text-xs uppercase tracking-widest text-muted-foreground">Contract Value</Label><Input type="number" min="0" step="0.01" value={form.contract_value} onChange={(e) => setForm({ ...form, contract_value: e.target.value })} className="border-border tabular-nums" /></div>
              <div className="space-y-1.5 col-span-2"><Label className="text-xs uppercase tracking-widest text-muted-foreground">Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="border-border" /></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="client-submit-btn">{editing ? "Save" : "Create"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
